import { React, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../App.css';
import { Grid, TextField, Box, Button, Paper, Stack, Typography, InputAdornment, IconButton, Modal, Fade, Backdrop } from '@mui/material';
import { Login, VisibilityOff, Visibility } from '@mui/icons-material';
import { message } from 'antd';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { authActions } from '../../store/auth-slice';
import { setLoaderMessage } from '../../utils/loaderManager';
import { fetchCurrentCompanyDetails, setStoredCompanyDetails } from '../../services/companyService';

const initialFValues = {
  id: 0,
  email: '',
  password: '',
}

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

function LoginForm() {
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(true);
  // Blocks a second send-otp request while one is in flight (state updates are too slow for this)
  const sendingOtpRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const displayLogo = localStorage.getItem('companyLogoBase64') || localStorage.getItem('companyLogoURL') || '';

  // Timer effect
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      setCanResendOtp(true);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const toggleShowPassword = () => {
    setShowPassword(showPassword => !showPassword);
  }

  const validate = () => {
    let temp = {};
    // Check if email is not empty
    temp.email = values.email ? "" : "This field is required.";
    // Check if password is not empty
    temp.password = values.password ? "" : "This field is required.";

    setErrors({
      ...temp
    });

    // Return true only if all validations pass
    return Object.values(temp).every(x => x === "");
  }

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
    setOtp('');
  }

  // Updated function to clear errors as the user types
  function handleInputChange(e) {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    });
    // Clear the specific error for the field being edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  }

  // Handle Enter key press in the OTP modal
  const handleOtpKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleOtpSubmit();
    }
  };

  const getCompanyLogoURL = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company-master/get-company-logo`, { credentials: 'include' });
      if (!response.ok) {
        // If no logo exists (404 or other error), clear any existing logo
        dispatch(authActions.clearCompanyLogo());
        localStorage.removeItem('companyLogoBase64');
        localStorage.removeItem('companyLogoURL');
        return;
      }
      const imageBlob = await response.blob();

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result;
        // Dispatch the Base64 string to the Redux store
        dispatch(authActions.setActiveCompanyLogoURL(base64data));
        // Store the Base64 string in localStorage for persistence
        localStorage.setItem('companyLogoBase64', base64data);
      };
      reader.readAsDataURL(imageBlob);
    } catch (error) {
      console.error("Error fetching company logo:", error);
      // Clear any existing logo on error
      dispatch(authActions.clearCompanyLogo());
      localStorage.removeItem('companyLogoBase64');
      localStorage.removeItem('companyLogoURL');
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sendingOtpRef.current) return;
    if (validate()) {
      sendingOtpRef.current = true;
      setIsLoading(true);
      setLoaderMessage('Sending OTP...');
      try {
        // Call send-otp API with email and password
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/company-master/send-otp`, {
          email: values.email,
          password: values.password
        });

        if (response.data.message === 'NOT_EXISTS') {
          messageApi.open({
            type: 'error',
            content: `User not Exists!`,
            style: {
              marginTop: '15vh',
            },
          });
        }
        else if (response.data.success) {
          // Open OTP modal if OTP sent successfully
          setOtpModalOpen(true);
          // Start the 2-minute timer (120 seconds)
          setOtpTimer(120);
          setCanResendOtp(false);
          messageApi.open({
            type: 'info',
            content: `OTP sent to your email!`,
            style: {
              marginTop: '15vh',
            },
          });
        } else {
          messageApi.open({
            type: 'error',
            content: `Invalid email or password!`,
            style: {
              marginTop: '15vh',
            },
          });
        }
      } catch (err) {
        console.log(err);
        
        // Check if it's a 401 Unauthorized error (wrong credentials)
        if (err.response && err.response.status === 401) {
          messageApi.open({
            type: 'error',
            content: `Invalid email or password!`,
            style: {
              marginTop: '15vh',
            },
          });
        } 
        // Check if it's a 404 Not Found error (user doesn't exist)
        else if (err.response && err.response.status === 404) {
          messageApi.open({
            type: 'error',
            content: `User not Exists!`,
            style: {
              marginTop: '15vh',
            },
          });
        }
        // Account deactivated by the company admin
        else if (err.response && err.response.status === 403) {
          messageApi.open({
            type: 'error',
            content: err.response.data?.message || 'Your account has been deactivated.',
            style: {
              marginTop: '15vh',
            },
          });
        }
        // For any other error
        else {
          messageApi.open({
            type: 'error',
            content: `An error occurred. Please try again.`,
            style: {
              marginTop: '15vh',
            },
          });
        }
      } finally {
        sendingOtpRef.current = false;
        setIsLoading(false);
      }
    }
  }

  const handleOtpSubmit = async () => {
    if (!otp) {
      messageApi.open({
        type: 'error',
        content: `Please enter the OTP!`,
        style: {
          marginTop: '15vh',
        },
      });
      return;
    }

    setIsLoading(true);
    setLoaderMessage('Verifying OTP...');
    try {
      // Verify OTP with the backend using email, password and OTP
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/company-master/verify-otp`, {
        email: values.email,
        password: values.password,
        otp: otp
      });

      if (response.data.success) {
        messageApi.open({
          type: 'success',
          content: `Successfully Logged in!`,
          style: {
            marginTop: '15vh',
          },
        });
        dispatch(authActions.setLogin());
        dispatch(authActions.setActiveUserName(response.data.activeUserName));
        dispatch(authActions.setActiveUserRole(response.data.activeUserRole));
        dispatch(authActions.setActiveAccess({
          accessRoleId: response.data.activeAccessRoleId,
          accessRoleName: response.data.activeAccessRoleName,
          permissions: response.data.activePermissions,
        }));
        localStorage.setItem('activeUserName', response.data.activeUserName || '');
        localStorage.setItem('activeUserRole', response.data.activeUserRole || 'USER');
        localStorage.setItem('loginTimestamp', Date.now().toString());
        getCompanyLogoURL();
        const companyDetails = await fetchCurrentCompanyDetails();
        if (companyDetails) {
          dispatch(authActions.setActiveCompanyDetails(companyDetails));
          setStoredCompanyDetails(companyDetails);
        }
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      } else {
        messageApi.open({
          type: 'error',
          content: `Invalid OTP!`,
          style: {
            marginTop: '15vh',
          },
        });
      }
    } catch (err) {
      console.log(err);
      messageApi.open({
        type: 'error',
        content: `An error occurred. Please try again.`,
        style: {
          marginTop: '15vh',
        },
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleResendOtp = async () => {
    if (!canResendOtp) return;

    setIsLoading(true);
    setLoaderMessage('Resending OTP...');
    try {
      // Resend OTP using the same API as initial login
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/company-master/send-otp`, {
        email: values.email,
        password: values.password
      });

      if (response.data.success) {
        // Reset the timer
        setOtpTimer(120);
        setCanResendOtp(false);

        messageApi.open({
          type: 'success',
          content: `OTP resent to your email!`,
          style: {
            marginTop: '15vh',
          },
        });
      } else {
        messageApi.open({
          type: 'error',
          content: `Failed to resend OTP. Please try again.`,
          style: {
            marginTop: '15vh',
          },
        });
      }
    } catch (err) {
      console.log(err);
      
      // Check if it's a 401 Unauthorized error (wrong credentials)
      if (err.response && err.response.status === 401) {
        messageApi.open({
          type: 'error',
          content: `Invalid email or password!`,
          style: {
            marginTop: '15vh',
          },
        });
        // Close the OTP modal since credentials are invalid
        setOtpModalOpen(false);
      } 
      // Check if it's a 404 Not Found error (user doesn't exist)
      else if (err.response && err.response.status === 404) {
        messageApi.open({
          type: 'error',
          content: `User not Exists!`,
          style: {
            marginTop: '15vh',
          },
        });
        // Close the OTP modal since user doesn't exist
        setOtpModalOpen(false);
      }
      // For any other error
      else {
        messageApi.open({
          type: 'error',
          content: `Failed to resend OTP. Please try again.`,
          style: {
            marginTop: '15vh',
          },
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", padding: "50px", fontWeight: "500px", marginTop: "-80px" }}>
      {contextHolder}
      <Paper elevation={5} sx={{ width: "430px", backgroundColor: "#ffff", margin: "100px", borderRadius: '10px' }}>
        <Grid className="form-title" container spacing={1} justifyContent="center" alignItems="center" sx={{ pt: 3 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                <Login sx={{ color: '#1565c0', fontSize: '24px' }} />
                <Typography variant="subtitle2" sx={{ textAlign: "center", fontSize: '18px', fontWeight: 600, color: '#1565c0' }}>
                  Login to DigiCompany Payroll System
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ margin: "30px" }}>
            <TextField
              variant="standard"
              name="email"
              label={<RequiredLabel label="Email" />}
              size="small"
              value={values.email}
              onChange={handleInputChange}
              {...(errors.email && { error: true, helperText: errors.email })}
            />
            <TextField
              variant="standard"
              name="password"
              label={<RequiredLabel label="Password" />}
              size="small"
              type={showPassword ? "text" : "password"}
              value={values.password}
              onChange={handleInputChange}
              {...(errors.password && { error: true, helperText: errors.password })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Link to="/forgot-password" style={{ marginTop: "7px" }} className="link-blue">Forgot Password?</Link>

            <Grid container spacing={2} justifyContent="center" style={{ marginTop: "2px", marginLeft: "-15px", marginRight: "10px" }}>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="error"
                  size="medium"
                  fullWidth
                  disableElevation
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  fullWidth
                  disableElevation
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending OTP...' : 'Login'}
                </Button>
              </Grid>
            </Grid>
            <div style={{ marginTop: '12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <p style={{ color: '#475569', margin: '0', fontSize: '16px', lineHeight: 1.5 }}>
                  If you haven't set up your company for payroll and compliance yet, now is the time to do it!
                </p>
                <Link
                  to="/create-account"
                  className="link-blue"
                  style={{ color: '#0b418e', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', fontSize: '16px', lineHeight: 1.5 }}
                >
                  Create your account today!
                </Link>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
              <div style={{ flexGrow: 1, borderTop: '1px solid #ccc' }}></div>
              <span style={{ margin: '0 10px', color: '#888', fontSize: '14px' }}>OR</span>
              <div style={{ flexGrow: 1, borderTop: '1px solid #ccc' }}></div>
            </div>

            <button
              type="button"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#333',
                marginBottom: '-10px',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.60 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <button
              type="button"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#333',
              }}
            >
              <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                <path fill="#F25022" d="M2.5 2.5H11V11H2.5z" />
                <path fill="#7FBA00" d="M13 2.5H21.5V11H13z" />
                <path fill="#00A4EF" d="M2.5 13H11V21.5H2.5z" />
                <path fill="#FFB900" d="M13 13H21.5V21.5H13z" />
              </svg>
              Continue with Microsoft
            </button>
          </Stack>
        </form>
      </Paper>

      {/* OTP Verification Modal */}
      <Modal
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={otpModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Enter OTP
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please enter the OTP sent to your email address
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              label="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              onKeyPress={handleOtpKeyPress}
              sx={{ mb: 2 }}
            />

            {/* Timer display */}
            {otpTimer > 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                OTP expires in: {formatTime(otpTimer)}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                onClick={handleResendOtp}
                color="primary"
                disabled={!canResendOtp || isLoading}
              >
                Resend OTP {otpTimer > 0 && `(${formatTime(otpTimer)})`}
              </Button>
              <Box>
                <Button onClick={() => setOtpModalOpen(false)} sx={{ mr: 1 }} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleOtpSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Verifying...' : 'Submit'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  )
}

export default LoginForm;
